import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./IosAppDevelopment.css";

export default function IosAppDevelopment() {
    const services = [
        {
            title: "Custom iOS App Development",
            description: "Build native iOS apps for startups, businesses, game studios, and product teams with polished UI, scalable APIs, secure architecture, and release-ready builds.",
            points: ["Native iOS apps", "Product architecture", "App Store builds"]
        },
        {
            title: "Business & Enterprise Apps",
            description: "Create secure iPhone and iPad apps for operations, field teams, CRM, booking, reporting, dashboards, workflow automation, and internal productivity.",
            points: ["Role-based access", "Dashboards", "Secure workflows"]
        },
        {
            title: "On-Demand iOS Apps",
            description: "Develop booking, delivery, marketplace, service, ecommerce, education, healthcare, gaming, and location-based iOS applications.",
            points: ["Maps & tracking", "Payments", "Notifications"]
        },
        {
            title: "iOS App Modernization",
            description: "Improve existing iOS apps with UI upgrades, performance optimization, API updates, bug fixing, analytics, and App Store compliance.",
            points: ["UI refresh", "Performance fixes", "API upgrades"]
        }
    ];

    const heroStats = [
        { value: "Native", label: "iOS Apps" },
        { value: "API", label: "Backend Ready" },
        { value: "QA", label: "Device Tested" }
    ];

    const capabilities = [
        "Swift",
        "Objective-C",
        "Xcode",
        "SwiftUI",
        "UIKit",
        "REST APIs",
        "Firebase",
        "Apple Sign-In",
        "Push Notifications",
        "Payment Gateway",
        "Apple Maps & GPS",
        "Authentication",
        "Analytics",
        "Offline Support",
        "In-App Purchases",
        "Game Center",
        "App Store Publishing",
        "QA Testing",
        "Maintenance"
    ];

    const appTypes = [
        "Ecommerce Apps",
        "Booking Apps",
        "Delivery Apps",
        "Healthcare Apps",
        "Education Apps",
        "Fintech Apps",
        "Enterprise Apps",
        "Game Apps",
        "Social Apps",
        "Media Apps",
        "Utility Apps"
    ];

    const productModules = [
        { title: "User Accounts", description: "Secure sign-up, login, profile management, roles, permissions, and session handling." },
        { title: "Admin Dashboard", description: "Manage users, content, orders, bookings, reports, settings, and app activity from one place." },
        { title: "Payments & Wallets", description: "Integrate payment gateways, subscriptions, wallet flows, invoices, refunds, and transaction history." },
        { title: "Maps & Location", description: "Add GPS, live tracking, geofencing, nearby search, delivery routes, and location-based features." },
        { title: "Notifications", description: "Use push notifications, transactional alerts, reminders, campaigns, and event-based messaging." },
        { title: "Game App Features", description: "Build leaderboards, achievements, in-app purchases, rewards, player profiles, matchmaking basics, and event tracking." },
        { title: "Analytics & Reports", description: "Track user behavior, app events, revenue, retention, conversion, and operational performance." }
    ];

    const engagementModels = [
        {
            title: "MVP iOS App",
            description: "Best for validating a new idea with essential features, clean UX, backend basics, QA, and a launch-ready first version.",
            items: ["Core user journey", "Essential APIs", "App Store support"]
        },
        {
            title: "Full Product Build",
            description: "Best for businesses that need a complete iOS product with admin panel, integrations, analytics, and scalable architecture.",
            items: ["Full feature set", "Backend & admin", "QA and release"]
        },
        {
            title: "App Upgrade & Support",
            description: "Best for existing apps that need bug fixing, UI improvement, performance work, API updates, maintenance, and new features.",
            items: ["Audit and fixes", "Feature upgrades", "Ongoing support"]
        }
    ];

    const process = [
        { step: "01", title: "Discovery", description: "We define app goals, users, features, integrations, backend needs, monetization, timeline, and launch plan." },
        { step: "02", title: "UX & Architecture", description: "We plan app flows, wireframes, UI direction, API contracts, data structure, permissions, and technical architecture." },
        { step: "03", title: "Development", description: "Our team builds the iOS app, backend integrations, admin tools, notifications, payments, analytics, and feature modules." },
        { step: "04", title: "QA & Optimization", description: "We test iPhone and iPad devices, iOS versions, performance, security, usability, crashes, edge cases, and App Store readiness." },
        { step: "05", title: "Launch & Support", description: "We support App Store publishing, monitoring, updates, bug fixes, new features, and long-term maintenance." }
    ];

    const benefits = [
        { icon: "fa-brands fa-apple", title: "iOS-Focused Delivery", description: "We build iOS apps around Apple platform behavior, device coverage, App Store rules, and user expectations." },
        { icon: "fa-solid fa-mobile-screen-button", title: "Clean Mobile UX", description: "Interfaces are designed for fast actions, readable screens, clear navigation, and responsive mobile behavior." },
        { icon: "fa-solid fa-server", title: "Backend Integration", description: "We connect APIs, databases, admin panels, authentication, payments, notifications, analytics, and cloud services." },
        { icon: "fa-solid fa-shield-check", title: "QA & Store Readiness", description: "We test app stability, performance, permissions, device compatibility, crashes, and release compliance." }
    ];

    const faqs = [
        {
            question: "What iOS app development services does Oengines provide?",
            answer: "Oengines provides custom iOS app development, UI/UX, backend integration, Firebase, APIs, payments, maps, notifications, QA, App Store publishing, and maintenance."
        },
        {
            question: "Do you build native iOS apps?",
            answer: "Yes. We build native iOS apps using Swift, Objective-C, Xcode, SwiftUI, UIKit, and modern Apple mobile development practices."
        },
        {
            question: "Can you build backend and admin panel with the iOS app?",
            answer: "Yes. We can build APIs, databases, admin dashboards, authentication, reporting, analytics, and cloud deployment along with the iOS app."
        },
        {
            question: "Can you publish the app on Apple App Store?",
            answer: "Yes. We help prepare release builds, store assets, app details, compliance checks, testing, and App Store publishing support."
        },
        {
            question: "Do you build iOS game apps?",
            answer: "Yes. We build iOS game apps with user profiles, rewards, leaderboards, Game Center, in-app purchases, analytics, backend APIs, QA, and App Store release support."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "iOS App Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "iOS App Development",
        "areaServed": "Worldwide",
        "description": "iOS app development services for native apps, business apps, game apps, on-demand apps, backend integrations, QA, App Store publishing, and maintenance.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>iOS App Development Company | Native iOS & Game Apps | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds native iOS apps and game apps with Swift, SwiftUI, UIKit, backend APIs, Firebase, payments, maps, notifications, QA, App Store publishing, and maintenance support." />
                <meta name="keywords" content="iOS app development company, iOS app development services, native iOS app development, iPhone app development, iPad app development, iOS game app development, Swift app development, mobile app development company" />
                <link rel="canonical" href="https://www.oengines.com/ios-app-development" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="ios-app-page">
                <section className="ios-app-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="ios-app-eyebrow">iOS App Development</span>
                                <h1>Build Native iOS Apps for Business, Games & Growth</h1>
                                <p>Oengines Games LLP develops iOS applications and game apps with polished UI/UX, reliable backend integrations, secure architecture, strong QA, App Store readiness, and long-term product support.</p>
                                <div className="ios-app-actions">
                                    <a href="/contact" className="as-btn">Start a Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="ios-app-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="ios-app-hero-stats">
                                    {heroStats.map((item) => (
                                        <div key={item.label}>
                                            <strong>{item.value}</strong>
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="ios-app-hero-card">
                                    <img src="assets/img/bg/hero-bg/app.jpg" alt="iOS app development" />
                                    <div>
                                        <strong>Native iOS Delivery</strong>
                                        <span>Swift, SwiftUI, UIKit, APIs, Firebase, game features, QA, App Store, and maintenance.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="ios-app-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>iOS Services</span>
                            <h2>iOS App Development Services for Modern Digital Products</h2>
                            <p>We help you build iOS apps that are usable, scalable, stable, and ready for real users across iPhone, iPad, and current iOS versions.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="ios-app-card">
                                        <h3>{service.title}</h3>
                                        <p>{service.description}</p>
                                        <ul>
                                            {service.points.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="ios-app-model-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>Development Options</span>
                            <h2>Choose the Right iOS App Development Path</h2>
                            <p>Whether you need a fast MVP, a complete product, or improvements to an existing app, we shape the delivery plan around your business stage.</p>
                        </div>
                        <div className="ios-app-model-grid">
                            {engagementModels.map((model) => (
                                <article className="ios-app-model-card" key={model.title}>
                                    <h3>{model.title}</h3>
                                    <p>{model.description}</p>
                                    <ul>
                                        {model.items.map((item) => <li key={item}>{item}</li>)}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="ios-app-tech-section space">
                    <div className="container">
                        <div className="ios-app-tech-panel">
                            <div className="ios-app-heading mb-0">
                                <span>Technology & Integrations</span>
                                <h2>Everything Needed for Production-Ready iOS Apps</h2>
                                <p>From UI and native development to APIs, cloud, analytics, payments, and publishing, we cover the full iOS product lifecycle.</p>
                            </div>
                            <div className="ios-app-tech-list">
                                {capabilities.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="ios-app-types-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>App Types</span>
                            <h2>iOS Apps We Can Build for Your Business</h2>
                        </div>
                        <div className="ios-app-types-list">
                            {appTypes.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    </div>
                </section>

                <section className="ios-app-module-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>Feature Modules</span>
                            <h2>iOS App Features We Commonly Build</h2>
                            <p>These modules can be combined based on your product type, user roles, business workflow, and launch priorities.</p>
                        </div>
                        <div className="ios-app-module-grid">
                            {productModules.map((module) => (
                                <article className="ios-app-module-card" key={module.title}>
                                    <h3>{module.title}</h3>
                                    <p>{module.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="ios-app-process-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>Development Process</span>
                            <h2>From iOS App Idea to App Store Launch</h2>
                        </div>
                        <div className="ios-app-process-grid">
                            {process.map((item) => (
                                <article className="ios-app-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="ios-app-benefit-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>iOS App Development Backed by Design, Backend & QA</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="ios-app-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="ios-app-faq-section space">
                    <div className="container">
                        <div className="ios-app-heading text-center">
                            <span>FAQ</span>
                            <h2>iOS App Development Questions</h2>
                        </div>
                        <div className="ios-app-faq-grid">
                            {faqs.map((item) => (
                                <article className="ios-app-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="ios-app-cta">
                    <div className="container">
                        <div className="ios-app-cta-box">
                            <div>
                                <span>Build for iOS</span>
                                <h2>Plan your iOS app with Oengines.</h2>
                                <p>Share your app idea, target users, required features, backend needs, and launch goals. We will help define the right development path.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

